import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowRight, Check, X, Star, Shield, Zap, MailCheck, MessageCircle,
  GraduationCap, Clock, Lock, ChevronDown, Sparkles,
} from 'lucide-react';
import SEO from '../components/SEO';
import { profileData } from '../mock';
import { track } from '../lib/analytics';

// Booking URL — user drops the paid ($30) TidyCal link in .env when TidyCal is set up
const BOOKING_URL =
  process.env.REACT_APP_CONSULT_BOOKING_URL ||
  'https://tidycal.com/akashtiwari/60-minute-meeting';

const LOGO_URL = '/logo.png';
const AKASH_IMG = profileData.image;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ---------- Sub-components (inlined for LP page-weight) ----------

const TopBar = () => (
  <header className="w-full bg-cream/90 backdrop-blur-md border-b border-line sticky top-0 z-40" data-testid="consult-topbar">
    <div className="container-x flex items-center justify-between py-3.5">
      <a href="/" className="flex items-center gap-2.5" data-testid="consult-logo-link">
        <img src={LOGO_URL} alt="Growth Hackeng" className="h-8 w-auto" />
        <span className="font-display text-base font-extrabold text-ink hidden sm:inline">Growth Hackeng</span>
      </a>
      <button
        onClick={() => { track('consult_booking_intent', { position: 'topbar' }); scrollToId('book-cta'); }}
        data-testid="consult-topbar-cta"
        className="btn-orange text-sm py-2 px-4"
      >
        Book $30 consult <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  </header>
);

const TrustPill = ({ children }) => (
  <div className="inline-flex items-center gap-2 rounded-full bg-cream-2 border border-line px-3.5 py-1.5 text-[11px] uppercase tracking-widest font-semibold text-ink">
    {children}
  </div>
);

const Hero = () => (
  <section className="container-x pt-10 md:pt-14 pb-16 md:pb-24" data-testid="consult-hero">
    <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
      {/* LEFT: copy + CTA */}
      <div className="lg:flex-1 max-w-2xl">
        <TrustPill>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--gh-orange)] animate-pulse" />
          For fitness · yoga · wellness creators with 10K+ list
        </TrustPill>

        <h1 className="font-display font-extrabold text-ink leading-[1.05] tracking-tight mt-6 text-4xl sm:text-5xl lg:text-[62px]">
          Turn your 10K list into a{' '}
          <span className="italic text-[var(--gh-orange)]">system that sells</span>{' '}
          while you teach.
        </h1>

        <p className="mt-6 text-lg text-muted-warm leading-relaxed max-w-xl">
          A private 60-minute call with Akash. We map your Email · Newsletter · Chatbot · Course
          stack, name the one bottleneck killing your revenue, and leave you with three moves
          you can ship this week — before the call ends.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('consult_booking_intent', { position: 'hero' })}
            data-testid="consult-hero-cta"
            className="btn-orange text-base px-6 py-3.5"
          >
            Book my $30 consult <ArrowRight className="w-4 h-4" />
          </a>
          <div className="text-xs text-muted-warm leading-snug">
            <p className="font-semibold text-ink">$30 · 60 minutes · full hour with Akash</p>
            <p>Refunded in full if I&apos;m not useful in the first 10 minutes.</p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-muted-warm">
          <span className="inline-flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 fill-[var(--gh-orange)] text-[var(--gh-orange)]" />
            <span className="font-semibold text-ink">5.0</span> on Upwork (16 verified reviews)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5 text-[var(--gh-orange)]" />
            60+ creators shipped, 12 countries
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[var(--gh-orange)]" />
            7+ yrs in Kit.com
          </span>
        </div>
      </div>

      {/* RIGHT: portrait + float badges */}
      <div className="lg:flex-1 w-full max-w-lg self-center lg:self-start relative">
        <div className="relative aspect-[4/5] rounded-[1.75rem] overflow-hidden bg-cream-2">
          <img
            src={AKASH_IMG}
            alt="Akash Tiwari — Growth Systems strategist"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src =
                'https://images.unsplash.com/photo-1649433658557-54cf58577c68?w=900&q=80';
            }}
          />
        </div>
        <div className="absolute -top-4 -left-4 float-card px-4 py-3 max-w-[220px] hidden md:block">
          <p className="text-[10px] uppercase tracking-widest text-muted-warm font-semibold">Available</p>
          <p className="text-sm font-bold text-ink leading-tight">Q1 2026 · 2 new client seats</p>
        </div>
        <div className="absolute -bottom-4 -right-4 float-card px-4 py-3 max-w-[240px] hidden md:block">
          <p className="text-[10px] uppercase tracking-widest text-muted-warm font-semibold">Latest result</p>
          <p className="text-sm font-bold text-ink leading-tight">17% → 87% open rate in 5 mo</p>
          <p className="text-[11px] text-muted-warm">— Amber, wellness newsletter</p>
        </div>
      </div>
    </div>
  </section>
);

const PlatformStrip = () => {
  const platforms = [
    { name: 'Kit.com', logo: '/assets/marquee/kit-logo-soft-black.svg' },
    { name: 'ActiveCampaign', logo: '/assets/marquee/activecampaign-logo.webp' },
    { name: 'Mailchimp', logo: '/assets/marquee/mailchimp.webp' },
    { name: 'Beehiiv', logo: '/assets/marquee/beehiiv.webp' },
    { name: 'ManyChat', logo: '/assets/marquee/manychat-logo.webp' },
    { name: 'Zapier', logo: '/assets/marquee/zapier_logo.webp' },
    { name: 'Make', logo: '/assets/marquee/make-logo.webp' },
  ];
  return (
    <section
      className="py-8 md:py-10 bg-cream border-y border-line"
      data-testid="consult-platform-strip"
    >
      <div className="container-x">
        <p className="text-center text-[10px] md:text-[11px] uppercase tracking-[0.25em] font-semibold text-muted-warm">
          7+ years building in the tools your business already runs on
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-5 md:gap-x-14">
          {platforms.map((p) => (
            <img
              key={p.name}
              src={p.logo}
              alt={`${p.name} — platform Akash builds in`}
              title={p.name}
              loading="lazy"
              decoding="async"
              width="110"
              height="36"
              className="h-7 md:h-8 w-auto object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
              style={{ mixBlendMode: 'multiply' }}
              data-testid={`consult-platform-${p.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const Problem = () => {
  const pains = [
    {
      title: 'You built the audience. The revenue didn\'t follow.',
      body: 'Your list is 10K+ and still growing. But course launches only spike once a quarter, DMs pile up unanswered, and there\'s no evergreen engine underneath.',
    },
    {
      title: 'Your open rate is fine. Your inbox rate isn\'t.',
      body: 'Gmail promotions tab is eating your sends. You know it because your best subscribers say they "missed" the last three emails.',
    },
    {
      title: 'You\'re the bottleneck for every reply, DM, and student question.',
      body: 'No welcome sequence, no chatbot triage, no course-drip. Every new subscriber lands in a cold list you\'ll "get around to" segmenting.',
    },
  ];
  return (
    <section className="bg-cream-2 py-16 md:py-24" data-testid="consult-problem">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">The gap</p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-ink mt-3 leading-tight">
            10K subscribers, and the revenue still leaks.
          </h2>
          <p className="mt-4 text-muted-warm text-base">
            Three patterns I see in almost every wellness, yoga, and fitness creator who books this call:
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {pains.map((p, i) => (
            <div
              key={p.title}
              className="rounded-2xl bg-cream border border-line p-6"
              data-testid={`consult-pain-${i}`}
            >
              <span className="inline-flex w-9 h-9 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] items-center justify-center font-display font-bold">
                {i + 1}
              </span>
              <h3 className="mt-4 font-display font-bold text-ink text-lg leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm text-muted-warm leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Systems = () => {
  const systems = [
    {
      icon: MailCheck,
      label: 'Email',
      title: 'Deliverability + welcome flow that actually opens',
      bullets: [
        'SPF / DKIM / DMARC audit for Gmail promotions escape',
        'Warm-up plan for your sending domain',
        '5-touch welcome sequence written for your niche voice',
      ],
    },
    {
      icon: Zap,
      label: 'Newsletter',
      title: 'Newsletter monetization blueprint',
      bullets: [
        'Weekly cadence + content pillars mapped to your offer',
        'Native ad slot vs. course-drip vs. sponsor comparison',
        'Sales funnel: newsletter → checkout with UTM tracking',
      ],
    },
    {
      icon: MessageCircle,
      label: 'Chatbots',
      title: 'DM + email chatbot triage',
      bullets: [
        'Instagram / ManyChat lead-capture flow',
        'Auto-tag by intent (student · potential client · fan)',
        'Sync into Kit.com so email + chat share one profile',
      ],
    },
    {
      icon: GraduationCap,
      label: 'Courses',
      title: 'Course drip + student retention',
      bullets: [
        'Cohort vs evergreen: which fits your teaching cadence',
        'Onboarding sequence that reduces refunds and ghosts',
        'Upsell path from course → 1:1 or retainer',
      ],
    },
  ];
  return (
    <section className="py-16 md:py-24" data-testid="consult-systems">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">The 60-min consult</p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-ink mt-3 leading-tight">
            Four systems. One conversation. Your list, mapped end-to-end.
          </h2>
          <p className="mt-4 text-muted-warm">
            We spend the call on the parts of your stack that leak the most revenue. You leave with a
            plan and three actions you can ship this week — no deck, no follow-up email spam.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {systems.map((s, i) => {
            const Icon = s.icon;
            const highlight = i === 1; // Newsletter card is highlighted orange
            return (
              <div
                key={s.label}
                data-testid={`consult-system-${s.label.toLowerCase()}`}
                className={`rounded-2xl p-7 border ${
                  highlight
                    ? 'bg-[var(--gh-orange)] text-[var(--gh-cream)] border-[var(--gh-orange)]'
                    : 'bg-cream border-line'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex w-11 h-11 rounded-xl items-center justify-center ${
                      highlight ? 'bg-white/15 text-white' : 'bg-[var(--gh-orange-soft)] text-[var(--gh-orange)]'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </span>
                  <p className={`text-[11px] uppercase tracking-widest font-bold ${highlight ? 'text-white/80' : 'text-muted-warm'}`}>
                    {s.label}
                  </p>
                </div>
                <h3 className={`font-display text-xl font-bold mt-4 leading-snug ${highlight ? '' : 'text-ink'}`}>
                  {s.title}
                </h3>
                <ul className={`mt-4 space-y-2 text-sm ${highlight ? 'text-[var(--gh-cream)]' : 'text-muted-warm'}`}>
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? 'text-white' : 'text-[var(--gh-orange)]'}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WhyPaid = () => (
  <section className="bg-ink text-[var(--gh-cream)] py-16 md:py-20" data-testid="consult-why-paid">
    <div className="container-x max-w-3xl">
      <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">Why $30, not free</p>
      <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-3 leading-tight">
        The $30 isn&apos;t the price. It&apos;s the filter.
      </h2>
      <p className="mt-5 text-base opacity-90 leading-relaxed">
        Free calls fill up with tire-kickers. A paid slot means the person on the other side of the
        Zoom has skin in the game — and I can spend the full 60 minutes on real work instead of
        qualifying. If we go past 30, we go past 30. No upsell, no pitch deck.
      </p>
      <p className="mt-4 text-base opacity-90 leading-relaxed">
        If the first 10 minutes aren&apos;t useful, PayPal refunds you the $30 automatically. That has
        never happened yet — and I&apos;d like to keep it that way.
      </p>
    </div>
  </section>
);

const ProofStrip = () => {
  const wins = [
    { number: '17% → 87%', label: 'open rate, 5 months', who: 'Amber · wellness newsletter' },
    { number: '4×', label: 'sales on birthday promo', who: 'Vishakha · retainer client' },
    { number: '60%', label: 'less operational overhead', who: 'Francisco · migration + rebuild' },
  ];
  return (
    <section className="py-16 md:py-20 bg-cream-2" data-testid="consult-proof">
      <div className="container-x">
        <p className="text-center text-[11px] uppercase tracking-widest font-semibold text-muted-warm">
          Real numbers from real creators — not stock testimonials
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {wins.map((w) => (
            <div
              key={w.who}
              className="rounded-2xl bg-cream border border-line p-6 text-center"
              data-testid={`consult-win-${w.who.split(' ')[0].toLowerCase()}`}
            >
              <p className="font-display text-4xl font-extrabold text-ink leading-none">{w.number}</p>
              <p className="mt-2 text-sm text-ink font-semibold">{w.label}</p>
              <p className="mt-3 text-xs text-muted-warm">{w.who}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AfterYouPay = () => {
  const steps = [
    { n: '01', title: 'Pay $30 via PayPal', body: 'One click. No account needed — PayPal supports guest checkout with any card.' },
    { n: '02', title: 'Pick your slot', body: 'Calendar opens automatically. Pick a time in the next 7 days. Zoom link + prep questions land in your inbox.' },
    { n: '03', title: 'Show up. We do the work.', body: 'No deck, no small talk. 30 focused minutes on your stack. You leave with three actions to ship this week.' },
  ];
  return (
    <section className="py-16 md:py-24" data-testid="consult-timeline">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">What happens next</p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-ink mt-3 leading-tight">
            Three steps between now and your first system win.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {steps.map((s) => (
            <div key={s.n} className="rounded-2xl bg-cream-2 border border-line p-6" data-testid={`consult-step-${s.n}`}>
              <p className="font-display text-[42px] font-extrabold text-[var(--gh-orange)] leading-none">{s.n}</p>
              <h3 className="font-display text-lg font-bold text-ink mt-3">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-warm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Qualifier = () => (
  <section className="py-16 md:py-20 bg-cream-2" data-testid="consult-qualifier">
    <div className="container-x">
      <div className="max-w-2xl">
        <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">Straight talk</p>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold text-ink mt-3 leading-tight">
          Book this call if…
        </h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5 mt-10">
        <div className="rounded-2xl bg-cream border border-line p-7" data-testid="consult-for">
          <div className="inline-flex w-10 h-10 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] items-center justify-center">
            <Check className="w-5 h-5" />
          </div>
          <h3 className="font-display font-bold text-ink text-lg mt-4">This is for you if</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-ink">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--gh-orange)] shrink-0" />You run a fitness, yoga, or wellness practice with 10K+ subscribers</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--gh-orange)] shrink-0" />You already sell something (course, membership, retreats, coaching)</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--gh-orange)] shrink-0" />Your list feels like a chore, not an asset</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[var(--gh-orange)] shrink-0" />You want a specific plan, not another marketing course</li>
          </ul>
        </div>
        <div className="rounded-2xl bg-cream border border-line p-7" data-testid="consult-not-for">
          <div className="inline-flex w-10 h-10 rounded-xl bg-cream-3 text-muted-warm items-center justify-center">
            <X className="w-5 h-5" />
          </div>
          <h3 className="font-display font-bold text-ink text-lg mt-4">This is not for you if</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-muted-warm">
            <li className="flex items-start gap-2"><X className="w-4 h-4 mt-0.5 shrink-0" />You have fewer than 5,000 subscribers (I can&apos;t move the needle yet)</li>
            <li className="flex items-start gap-2"><X className="w-4 h-4 mt-0.5 shrink-0" />You want done-for-you copy in an hour (this is strategy, not writing)</li>
            <li className="flex items-start gap-2"><X className="w-4 h-4 mt-0.5 shrink-0" />You&apos;re looking for a magic subject line, not a system</li>
            <li className="flex items-start gap-2"><X className="w-4 h-4 mt-0.5 shrink-0" />You aren&apos;t ready to actually implement anything</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const FaqItem = ({ q, a, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line py-5" data-testid={`consult-faq-${id}`}>
      <button
        className="w-full flex items-center justify-between gap-4 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-display text-base md:text-lg font-bold text-ink">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-warm shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <p className="mt-3 text-sm md:text-base text-muted-warm leading-relaxed">{a}</p>}
    </div>
  );
};

const FAQ = () => {
  const items = [
    { id: 1, q: 'Why paid and not free like everyone else?', a: 'Free calls attract browsers. A $30 slot means the person on the call is serious enough to spend real money — so I can spend the full 30 on strategy, not screening.' },
    { id: 2, q: "What if it doesn't help me?", a: 'PayPal refunds the full $30 automatically if the first 10 minutes aren\'t useful. No form, no email. Just close the call — I\'ll issue the refund.' },
    { id: 3, q: 'How long does the call actually run?', a: 'A full 60-minute block is booked and held for you. Most calls end right around the hour because that\'s what the work needs. If we need 5–10 more minutes to close a loop, I stay on. No extra charge.' },
    { id: 4, q: 'Do I get a recording or notes?', a: 'Zoom recording + a short 3-bullet action list emailed within 24 hours of the call. Free.' },
    { id: 5, q: 'Is this the same as the free audit on the main site?', a: 'No. The free audit is an async written brief. This is a live, real-time strategy call with screen-share, back-and-forth, and a plan you leave with. Different product.' },
    { id: 6, q: "What if my list is smaller than 10K?", a: "Don't book this. Email me instead (akash@growthhackeng.com) — I'll point you to a better next step for your list size. No pressure, no upsell." },
  ];
  return (
    <section className="py-16 md:py-24" data-testid="consult-faq">
      <div className="container-x max-w-3xl">
        <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">Questions</p>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold text-ink mt-3 leading-tight">
          Six things I get asked every week.
        </h2>
        <div className="mt-8">
          {items.map((it) => <FaqItem key={it.id} {...it} />)}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section
    id="book-cta"
    className="py-20 md:py-28 bg-[var(--gh-orange)] text-[var(--gh-cream)]"
    data-testid="consult-final-cta"
  >
    <div className="container-x max-w-3xl text-center">
      <p className="text-[11px] uppercase tracking-widest font-semibold text-white/80">
        Book the call
      </p>
      <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-4 leading-tight">
        60 minutes. Three actions.<br />One system that stops leaking revenue.
      </h2>
      <p className="mt-6 text-base md:text-lg opacity-95 max-w-xl mx-auto">
        Pay $30 via PayPal. Pick a slot in the next 7 days. Refunded if the first 10 minutes
        aren&apos;t the most useful conversation you&apos;ve had about your list this year.
      </p>
      <div className="mt-10 flex flex-col items-center gap-4">
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track('consult_booking_intent', { position: 'final_cta' })}
          data-testid="consult-final-cta-btn"
          className="inline-flex items-center gap-2 rounded-full bg-ink text-[var(--gh-cream)] hover:bg-[var(--gh-ink-soft)] transition-all font-semibold px-8 py-4 text-base"
        >
          Book my $30 consult <ArrowRight className="w-4 h-4" />
        </a>
        <p className="text-xs opacity-80 flex items-center gap-2">
          <Lock className="w-3 h-3" /> Secure PayPal checkout · $30 refundable in the first 10 min
        </p>
      </div>
    </div>
  </section>
);

// Fallback lead-capture form for visitors who won't book on first visit.
// Reuses the existing /api/contact endpoint with a distinct `source` label.
const FallbackForm = () => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const startedAt = useRef(Date.now());
  const [status, setStatus] = useState('idle'); // idle | sending | ok | error
  const [errMsg, setErrMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrMsg('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName: '',
          email,
          message: 'Consult LP fallback — not ready to book, wants follow-up.',
          honeypot,
          elapsedMs: Date.now() - startedAt.current,
          source: 'consult-lp-fallback',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Something broke on our end. Please try again.');
      }
      setStatus('ok');
      track('contact_form_submit', { source: 'consult_lp_fallback' });
      setFirstName('');
      setEmail('');
    } catch (err) {
      setErrMsg(err.message);
      setStatus('error');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-cream-2" data-testid="consult-fallback-form">
      <div className="container-x max-w-2xl">
        <div className="rounded-[1.75rem] bg-cream border border-line p-8 md:p-10">
          <p className="text-[11px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">
            Not ready to book yet?
          </p>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-ink mt-3 leading-tight">
            Send me your details. I&apos;ll email you a free 3-minute system check.
          </h2>
          <p className="mt-3 text-sm text-muted-warm">
            No spam, no drip funnel. One useful reply from a human (me) within 24 hours.
          </p>

          {status === 'ok' ? (
            <div
              className="mt-6 rounded-2xl bg-[var(--gh-orange-soft)] border border-[var(--gh-orange)] border-dashed p-5"
              data-testid="consult-fallback-success"
            >
              <p className="font-semibold text-ink">Got it. Check your inbox in the next 24 hours.</p>
              <p className="text-sm text-muted-warm mt-1">
                If it doesn&apos;t land, ping akash@growthhackeng.com directly.
              </p>
            </div>
          ) : (
            <form className="mt-6 grid gap-3" onSubmit={submit} noValidate>
              {/* honeypot — invisible */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
              />
              <input
                type="text"
                required
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="consult-fallback-name"
                className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
              />
              <input
                type="email"
                required
                placeholder="Email you actually read"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="consult-fallback-email"
                className="w-full px-4 py-3 rounded-2xl bg-cream-2 border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
              />
              <button
                type="submit"
                disabled={status === 'sending' || !firstName || !email}
                data-testid="consult-fallback-submit"
                className="btn-orange justify-center w-full py-3 mt-1 disabled:opacity-60"
              >
                {status === 'sending' ? 'Sending…' : 'Send me the free check'} <ArrowRight className="w-4 h-4" />
              </button>
              {status === 'error' && (
                <p className="text-xs text-red-600" data-testid="consult-fallback-error">{errMsg}</p>
              )}
              <p className="text-[11px] text-muted-warm text-center mt-1">
                We never share your email. Reply &quot;unsubscribe&quot; any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const MinimalFooter = () => (
  <footer className="py-10 bg-cream border-t border-line" data-testid="consult-footer">
    <div className="container-x flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2.5">
        <img src={LOGO_URL} alt="Growth Hackeng" className="h-7 w-auto" />
        <span className="font-display text-sm font-bold text-ink">Growth Hackeng</span>
      </div>
      <p className="text-xs text-muted-warm">
        © {new Date().getFullYear()} Growth Hackeng · Akash Tiwari · akash@growthhackeng.com
      </p>
      <a
        href="/"
        className="text-xs text-muted-warm hover:text-ink underline underline-offset-4"
        data-testid="consult-footer-home-link"
      >
        Full site
      </a>
    </div>
  </footer>
);

// Sticky mobile CTA bar
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      className={`md:hidden fixed bottom-0 left-0 right-0 z-50 bg-cream/95 backdrop-blur-md border-t border-line px-4 py-3 transition-transform ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      data-testid="consult-sticky-cta"
    >
      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track('consult_booking_intent', { position: 'sticky_mobile' })}
        className="btn-orange w-full justify-center py-3"
        data-testid="consult-sticky-cta-btn"
      >
        <Clock className="w-4 h-4" /> Book my $30 consult
      </a>
    </div>
  );
};

// ---------- Page ----------

const Consult = () => {
  useEffect(() => {
    document.body.style.background = 'var(--gh-cream)';
  }, []);

  return (
    <>
      <SEO
        title="Book a $30 Growth-Systems Consult — For Fitness, Yoga & Wellness Creators (10K+)"
        description="60-minute paid consultation with Akash Tiwari. Map your Email, Newsletter, Chatbot and Course systems. Leave with 3 actions you can ship this week. $30 refundable."
        canonical="/consult"
        noindex
      />
      <div data-testid="consult-page" className="min-h-screen">
        <TopBar />
        <Hero />
        <PlatformStrip />
        <Problem />
        <Systems />
        <WhyPaid />
        <ProofStrip />
        <AfterYouPay />
        <Qualifier />
        <FAQ />
        <FinalCTA />
        <FallbackForm />
        <MinimalFooter />
        <StickyMobileCTA />
      </div>
    </>
  );
};

export default Consult;
